import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Heart, Stethoscope, Building2, Plane, Car, GraduationCap,
  Clock, Wallet, Home, Baby, Dumbbell, Coffee, Wifi, Gift, Check
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface BenefitsDisplayCardProps {
  benefits: string[];
  className?: string;
}

// Map benefit names to icons
const benefitIcons: Record<string, React.ReactNode> = {
  'health insurance': <Stethoscope className="w-3.5 h-3.5" />,
  'medical': <Stethoscope className="w-3.5 h-3.5" />,
  'dental': <Stethoscope className="w-3.5 h-3.5" />,
  'vision': <Stethoscope className="w-3.5 h-3.5" />,
  '401k': <Wallet className="w-3.5 h-3.5" />,
  'retirement': <Wallet className="w-3.5 h-3.5" />,
  'pension': <Wallet className="w-3.5 h-3.5" />,
  'pto': <Clock className="w-3.5 h-3.5" />,
  'paid time off': <Clock className="w-3.5 h-3.5" />,
  'vacation': <Plane className="w-3.5 h-3.5" />,
  'travel': <Plane className="w-3.5 h-3.5" />,
  'relocation': <Home className="w-3.5 h-3.5" />,
  'housing': <Home className="w-3.5 h-3.5" />,
  'company car': <Car className="w-3.5 h-3.5" />,
  'vehicle': <Car className="w-3.5 h-3.5" />,
  'training': <GraduationCap className="w-3.5 h-3.5" />,
  'education': <GraduationCap className="w-3.5 h-3.5" />,
  'tuition': <GraduationCap className="w-3.5 h-3.5" />,
  'parental leave': <Baby className="w-3.5 h-3.5" />,
  'maternity': <Baby className="w-3.5 h-3.5" />,
  'paternity': <Baby className="w-3.5 h-3.5" />,
  'gym': <Dumbbell className="w-3.5 h-3.5" />,
  'fitness': <Dumbbell className="w-3.5 h-3.5" />,
  'wellness': <Heart className="w-3.5 h-3.5" />,
  'meals': <Coffee className="w-3.5 h-3.5" />,
  'food': <Coffee className="w-3.5 h-3.5" />,
  'remote': <Wifi className="w-3.5 h-3.5" />,
  'flexible': <Clock className="w-3.5 h-3.5" />,
  'bonus': <Gift className="w-3.5 h-3.5" />,
  'stock': <Building2 className="w-3.5 h-3.5" />,
  'equity': <Building2 className="w-3.5 h-3.5" />,
};

const getIconForBenefit = (benefit: string): React.ReactNode => {
  const lower = benefit.toLowerCase();
  for (const [key, icon] of Object.entries(benefitIcons)) {
    if (lower.includes(key)) return icon;
  }
  return <Check className="w-3.5 h-3.5" />;
};

export const BenefitsDisplayCard: React.FC<BenefitsDisplayCardProps> = ({
  benefits,
  className
}) => {
  if (!benefits || benefits.length === 0) return null;

  return (
    <Card className={cn("border-amber-200/60 dark:border-amber-700/40 bg-gradient-to-br from-amber-50/50 to-orange-50/30 dark:from-amber-900/20 dark:to-orange-900/10", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-700 dark:text-amber-400">
          <Heart className="w-4 h-4" />
          Benefits & Perks
          <Badge variant="secondary" className="text-[10px] ml-auto bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            {benefits.length} included
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {benefits.map((benefit, idx) => (
            <div 
              key={idx}
              className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg text-sm"
            >
              <div className="text-amber-600 dark:text-amber-400">
                {getIconForBenefit(benefit)}
              </div>
              <span className="text-xs truncate">{benefit}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default BenefitsDisplayCard;
